import React, { useState, useEffect, lazy, Suspense, useCallback, useMemo } from "react";
import { isEmpty } from "lodash";
import { connect, useDispatch, useSelector } from "react-redux";
import mixpanel from "mixpanel-browser";

import { change } from "store/root/actions";
import { actions as modelActions } from "features/Model/_ducks";
import { setSaveTimeout } from "../_ducks/actions";

import { saveModel } from "../_ducks/thunk";

import { formSelector } from "store/form/selectors";
import { selectUpgradePackages } from "features/Model/Upgrades/_ducks/selectors";

import { useStateSelector } from "utils/field";
import { timeDifference } from "utils";
import { hasInsightsAccess } from "utils/insights";
import { useTourActive } from "utils/tours";
import {
    useLayout,
    DEFAULT_MODEL_TAB,
    isModelTabPath,
} from "hoc/Layout/LayoutProvider";

import Tabs from "components/Tabs";
import {
    useTabCompletions,
    useSimulationCompletion,
    getColorByPercentage,
    APP_COLORS,
    ChecklistPopover,
} from "components/ModelProgressBar";
import TopButtons from "../TopButtons";
import ModelTopBar from "../ModelTopBar";
import ModelHeaderTooltips from "../ModelTopBar/ModelHeaderTooltips";
import Checklist from "../Checklist/container";
import Settings from "../Settings";
import CompositeRCalc from "../Enclosure/CompositeRCalc";
import FoundationConfigDrawer from "../Enclosure/FoundationConfigDrawer";
import { EnclosureFilterProvider } from "../Enclosure/useEnclosureFilter.jsx";
import DrawingNew from "../DrawingNew";
import ImageGallery from "../ImageGallery";
import ProjectPathChecklist from "../ProjectPathChecklist";
import Dialog, { CloseDialog } from "components/Dialog";
import VersionControlDrawer from "features/Insights/VersionControlDrawer";

import classes from "../style.module.scss";

import EnclosureIcon from "assets/images/icons/Tab-Enclosure.svg";
import AirIcon from "assets/images/icons/Tab-Air.svg";
import GenerationIcon from "assets/images/icons/Generation.svg";
import GeneralIcon from "assets/images/icons/Tab-General.svg";
import SummaryIcon from "assets/images/icons/Tab-Summary.svg";
import UpgradeIcon from "assets/images/icons/Upgrades.svg";

const Enclosure = lazy(() => import("../Enclosure/index"));
const General = lazy(() => import("../General"));
const Review = lazy(() => import("../Review"));
const Airtightness = lazy(() => import("../Airtightness"));
const Mechanicals = lazy(() => import("../Mechanicals"));
const Upgrades = lazy(() => import("../Upgrades"));

const { clearMissingCodeWarning } = modelActions;

const ModelTabs = ({
    modelId,
    history,
    pathCallback,
    change,
    invalid,
    error,
    // pristine,
    nonErsUser,
    saveTimeout,
    modelDir,
    uid,
    codeLib,
    CHBAProjectChecklistOpen,
    toggleProjectChecklistOpen,
    missingCodes,
    VOLTA_DEV,
    isNewSnapshot,
    fetchModelData,
    isSyncing,
    setSubmitting,
    submitting,
    saveModel,
}) => {
    const dispatch = useDispatch();
    const isTourActive = useTourActive();

    const [drawingOpen, toggleDrawingOpen] = useState(false);
    const [checklistOpen, toggleChecklist] = useState(false);
    const [settingsOpen, toggleSettings] = useState(false);
    const [warningOpen, toggleWarning] = useState(false);
    const [versionHistoryOpen, toggleVersionHistory] = useState(false);

    const {
        model: { modelDetailsToCompare = {}, modelDetails = {} },
    } = useSelector(formSelector);

    const {
        modelTabActive,
        setModelTabActive,
    } = useLayout();


    const { name, createdAt = "", lastEdited = {} } = modelDetailsToCompare;
    const { owner = {}, writePermission = [], simulationStatus = "IDLE", isInsightsActive = false } = modelDetails;

    const hasAccess = hasInsightsAccess({ isInsightsActive, createdAt });

    const latestFormModelData = useStateSelector("model.modelData") || {}; //this is needed because sometimes the form is reinitialized just before saving

    const { name: ownerName = "", uid: ownerId = uid } = owner;
    const { name: editedName, datetime: editedDateTime } = lastEdited;

    const { program: { class: programClass = "" } = {} } = latestFormModelData;

    const disableSimulation = nonErsUser && programClass !== "";

    const readOnly = ownerId !== uid && !writePermission.includes(uid);

    useEffect(() => {
        if (!isEmpty(missingCodes) && !warningOpen) {
            toggleWarning(true);
        }
    }, [missingCodes]);

    const activeModelTab = isModelTabPath(modelTabActive) ? modelTabActive : DEFAULT_MODEL_TAB;

    // Update tab to match the current pathname when the browser's back button is pressed
    useEffect(() => {
        const [path, prevTab] = pathCallback().split(modelId);
        mixpanel.track("Main Tab Change", {
            Tab: prevTab,
        });

        if (isModelTabPath(prevTab)) {
            if (modelTabActive !== prevTab) {
                setModelTabActive(prevTab);
            }
            return;
        }

        const pathname = pathCallback();
        const basePath = `/models/${modelId}`;
        const restoredTab = isModelTabPath(modelTabActive) ? modelTabActive : DEFAULT_MODEL_TAB;

        if (pathname === basePath || pathname === `${basePath}/`) {
            history.replace(`${basePath}${restoredTab}`);
        }
    }, [pathCallback]);

    const handleSubmit = async () => {
        return saveModel(modelId, setSubmitting);
    };

    const saveModelWithCallback = useCallback(() => {
        saveModel(modelId, setSubmitting, true);
    }, [modelId, setSubmitting]);

    const { completions, checklists } = useTabCompletions();
    const { simulationCompletion, attentionChecks, modelComplete, hasSimulationResults } = useSimulationCompletion();

    const packages = useSelector(selectUpgradePackages) || {};
    const hasUpgradePackage = Object.keys(packages).filter((id) => id !== "base").length > 0;
    const upgradesCompletion = hasUpgradePackage ? 100 : 0;
    const upgradesChecks = useMemo(
        () => [{ label: "Upgrade package", completed: hasUpgradePackage }],
        [hasUpgradePackage]
    );

    const EMPTY_COLOR = APP_COLORS.grey;

    const tabIndicators = useMemo(() => {
        const makeIndicator = (percentage, items, label, useEmptyColor = false) => ({
            color: useEmptyColor && percentage === 0 ? EMPTY_COLOR : getColorByPercentage(percentage),
            hideIcon: percentage === 100,
            popoverContent: <ChecklistPopover items={items} label={label} rowLimit={3} />,
        });

        return {
            general: makeIndicator(completions.general, checklists.general, "General"),
            enclosure: makeIndicator(completions.enclosure, checklists.enclosure, "Enclosure"),
            airtightness: makeIndicator(completions.airtightness, checklists.airtightness, "Airtightness", true),
            systems: makeIndicator(completions.systems, checklists.systems, "Systems"),
            upgrades: makeIndicator(upgradesCompletion, upgradesChecks, "Upgrades", true),
        };
    }, [completions, checklists, upgradesCompletion, upgradesChecks]);

    return (
        <div>
            <ModelTopBar
                title={name}
                modelId={modelId}
                isInsightsActive={hasAccess}
                simulationCompletion={simulationCompletion}
                simulationChecks={attentionChecks}
                modelComplete={modelComplete}
                hasSimulationResults={hasSimulationResults}
                subtitle={
                    editedDateTime ? (
                        <span>
                            Last edited{" "}
                            <strong>
                                {timeDifference({
                                    start: new Date(),
                                    end: editedDateTime,
                                })}
                            </strong>{" "}
                            ago{editedName ? ` by ${editedName}.` : "."}
                        </span>
                    ) : (
                        <span>
                            Created{" "}
                            <strong>
                                {timeDifference({
                                    start: new Date(),
                                    end: createdAt,
                                })}
                            </strong>{" "}
                            ago{ownerName ? ` by ${ownerName}.` : "."}
                        </span>
                    )
                }
            >
                <TopButtons
                    submitting={submitting}
                    simulationStatus={simulationStatus}
                    toggleNewDrawingOpen={toggleDrawingOpen}
                    handleSubmit={handleSubmit}
                    invalid={invalid}
                    toggleChecklist={toggleChecklist}
                    saveTimeout={saveTimeout}
                    toggleSettings={toggleSettings}
                    isNewSnapshot={isNewSnapshot}
                    fetchModelData={fetchModelData}
                    isSyncing={isSyncing}
                    toggleVersionHistory={toggleVersionHistory}
                />
            </ModelTopBar>

            <div className={classes.modelWrapper}>
                {error && <p>{error}</p>}
                <EnclosureFilterProvider>
                <Tabs
                    tabActive={activeModelTab}
                    tabClassName={classes.tabWrapper}
                    tabNavClassName={classes.tabNavWrapper}
                    indicatorIconClassName={classes.tabProgressIcon}
                    tabNavProps={{ "data-general-tour": "tabs-row" }}
                    tabs={[
                        {
                            name: "General",
                            url: "/",
                            icon: GeneralIcon,
                            indicator: tabIndicators.general,
                            disabled: isTourActive,
                        },
                        {
                            name: "Enclosure",
                            url: "/enclosure",
                            icon: EnclosureIcon,
                            indicator: tabIndicators.enclosure,
                            disabled: isTourActive,
                        },
                        {
                            name: "Airtightness",
                            url: "/airtightness",
                            icon: AirIcon,
                            indicator: tabIndicators.airtightness,
                            disabled: isTourActive,
                        },
                        {
                            name: "Systems",
                            url: "/mechanicals",
                            icon: GenerationIcon,
                            indicator: tabIndicators.systems,
                            disabled: isTourActive,
                        },
                        {
                            name: "Upgrades",
                            url: "/upgrades",
                            icon: UpgradeIcon,
                            indicator: tabIndicators.upgrades,
                            disabled: isTourActive,
                            liProps: { "data-general-tour": "upgrades-tab" },
                        },
                        {
                            name: "Review",
                            url: "/review",
                            icon: SummaryIcon,
                            disabled: isTourActive,
                            liProps: { "data-general-tour": "review-tab" },
                        },
                    ]}
                    onChange={(e) => {
                        history.push(`/models/${modelId}${e}`);
                        mixpanel.track("Main Tab Change", {
                            Tab: e,
                        });
                        return setModelTabActive(e);
                    }}
                    stretchTabs
                    fallbackAction={saveModelWithCallback}
                >
                    <General history={history} change={change} modelSave={handleSubmit} readOnly={readOnly} />
                    <Enclosure
                        accessor={"model.modelData.dimensions"}
                        history={history}
                        change={change}
                        allowRoomByRoom={true}
                    />
                    <Airtightness accessor={"model.modelData.airTightness"} change={change} history={history} />
                    <Mechanicals
                        history={history}
                        change={change}
                        modelId={modelId}
                        fallbackAction={saveModelWithCallback}
                    />
                    <Upgrades
                        history={history}
                        change={change}
                        modelSave={handleSubmit}
                        uid={uid}
                        codeLib={codeLib}
                        readOnly={readOnly}
                        modelDir={modelDir}
                        modelId={modelId}
                        disableSimulation={disableSimulation}
                    />
                    <Review
                        history={history}
                        modelSave={handleSubmit}
                        isInvalid={invalid}
                        change={change}
                        readOnly={readOnly}
                        fallbackAction={saveModelWithCallback}
                    />
                </Tabs>
                </EnclosureFilterProvider>
            </div>
            {/* <Drawing
                    modelChange={change}
                    modelSave={handleSubmit}
                    modelName={name}
                    open={drawingOpen}
                    toggleOpen={toggleDrawing}
                /> */}
            <DrawingNew
                open={drawingOpen}
                toggleOpen={toggleDrawingOpen}
                modelChange={change}
                modelSave={handleSubmit}
                modelName={name}
                saving={submitting}
                isNewSnapshot={isNewSnapshot}
            />
            <ImageGallery handleSave={handleSubmit} isSavingModel={submitting} />
            <ProjectPathChecklist
                open={CHBAProjectChecklistOpen}
                toggleOpen={toggleProjectChecklistOpen}
                handleSave={handleSubmit}
            />
            <Settings change={change} open={settingsOpen} toggleOpen={toggleSettings} />
            <Checklist open={checklistOpen} toggleOpen={toggleChecklist} />
            <VersionControlDrawer isOpen={versionHistoryOpen} close={toggleVersionHistory} />
            <ModelHeaderTooltips showSyncTip={isSyncing || !isNewSnapshot} />
            <CompositeRCalc modelFormChange={change} />
            <FoundationConfigDrawer modelFormChange={change} />
            <Dialog open={warningOpen}>
                <CloseDialog
                    closeActions={() => {
                        dispatch(clearMissingCodeWarning());
                        toggleWarning(false);
                    }}
                />
                <div>
                    <p style={{ marginBottom: "1rem" }}>
                        Code reference(s) were detected in your model whose details were not properly saved. Please
                        re-select the following codes from the Type drop down menu for all components to which the
                        following codes are applied:
                    </p>
                    <ul>
                        {Object.keys(missingCodes).map((codeRef, ind) => (
                            <li key={ind}>{`${codeRef.split("-")[0]} code: ${missingCodes[codeRef]}`}</li>
                        ))}
                    </ul>
                </div>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (
    {
        user: {
            uid,
            name: username = "",
            codeLib = {},
            modelDir = {},
            nonErsUser = false,
            userPermissions: { VOLTA_DEV = false } = {},
        } = {},
        model: {
            modelId,
            modelDetails = {},
            modelTakeoff,
            dataEntryMode,
            modelData = {},
            missingCodes = {},
            error,
            saveTimeout,
            modelDetailsToCompare = {},
            linkedBaseline = {},
            lastSnapshot = {},
            modelDataToCompare = {},
            modelTakeoffToCompare = {},
        } = {},
    },
    { history }
) => {
    return {
        modelId,
        nonErsUser,
        history,
        codeLib,
        uid,
        modelData,
        modelDetails,
        modelDir,
        initialValues: {
            modelData,
            modelDetails,
        },
        error,
        modelTakeoff,
        dataEntryMode,
        saveTimeout,
        missingCodes,
        linkedBaseline,
        VOLTA_DEV,
        modelDetailsToCompare,
        lastSnapshot,
        modelDataToCompare,
        modelTakeoffToCompare,
        username,
    };
};

const mapDispatchToProps = (dispatch) => ({
    setSaveTimeout: (timeoutFunc) => dispatch(setSaveTimeout(timeoutFunc)),
    change: (path, value) => dispatch(change(path, value)),
    saveModel: (modelId, setSubmitting, isFromError) => dispatch(saveModel(modelId, setSubmitting, isFromError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModelTabs);
